import * as THREE from 'three'

export type XRMode = 'ar' | 'vr' | 'none'

/**
 * WebXRセッション管理。immersive-ar（パススルーMR導入・A3）を優先し、
 * 非対応なら immersive-vr にフォールバック。PCでは 'none'（プレビュー）。
 */
export class SessionManager {
  mode: XRMode = 'none'
  onEnd?: () => void

  constructor(private renderer: THREE.WebGLRenderer) {
    renderer.xr.enabled = true
    renderer.xr.setReferenceSpaceType('local-floor')
  }

  async detect(): Promise<{ ar: boolean; vr: boolean }> {
    if (!('xr' in navigator) || !navigator.xr) return { ar: false, vr: false }
    const [ar, vr] = await Promise.all([
      navigator.xr.isSessionSupported('immersive-ar').catch(() => false),
      navigator.xr.isSessionSupported('immersive-vr').catch(() => false),
    ])
    return { ar, vr }
  }

  /** XRセッションへ入場。成功したモードを返す */
  async enter(preferAr = true): Promise<XRMode> {
    if (!navigator.xr) return 'none'
    const support = await this.detect()
    const order: Array<'immersive-ar' | 'immersive-vr'> = []
    if (preferAr && support.ar) order.push('immersive-ar')
    if (support.vr) order.push('immersive-vr')
    if (!preferAr && support.ar) order.push('immersive-ar')

    for (const kind of order) {
      try {
        const session = await navigator.xr.requestSession(kind, {
          requiredFeatures: ['local-floor'],
          optionalFeatures: ['hand-tracking', 'bounded-floor'],
        })
        await this.renderer.xr.setSession(session)
        this.mode = kind === 'immersive-ar' ? 'ar' : 'vr'
        session.addEventListener('end', () => {
          this.mode = 'none'
          this.onEnd?.()
        })
        return this.mode
      } catch (e) {
        console.warn(`[xr] ${kind} セッション開始失敗:`, e)
      }
    }
    return 'none'
  }
}
